import { fetchCallCenterDevices, checkUsingHighSamplingRateDevice } from '@/entities/device';
import {
  getModelReasonOptions,
  splitReasonOptions,
  toOptionEntry,
  useRmaStore,
} from '@/entities/rma';
import type { RmaOption } from '@/entities/rma';
import { DEVICE, FACILITY_PROGRAMS, OTHER_RMA_REASONS, PROGRAM_REASON } from '@/shared/config';
import { useSetState } from '@/shared/lib/customHooks';
import classNames from 'classnames';
import _ from 'lodash';
import { useLayoutEffect, useMemo } from 'react';
import { t } from 'ttag';
import { INITIAL_ERRORS, NO_REPLACEMENT_OPTION } from '../../../config/addRmaDeviceModal';
import {
  filterDeviceIssue,
  getInitialDeviceIssue,
  getOptionsNeedingDescribe,
  handleValidate,
  withStoredDescribedReasons,
} from '../../../lib/addRmaDeviceModal';
import type { AddRMADeviceModalProps } from './addRmaDeviceModal.types';

/**
 * Maps an option onto a select option whose title repeats its label. CustomSelect shows the title of the picked option, so the title is what keeps the label visible once the option is selected.
 *
 * @param option - Option carrying its label and value.
 * @returns The label, value and title of the select option.
 */
const toSelectOption = ({ label, value }: Pick<RmaOption, 'label' | 'value'>) => ({
  label,
  value,
  title: label,
});

/**
 * Drives the add or edit RMA device modal: device search, replacement device, reasons, patient
 * impact and date of event for one device issue. Reads the RMA option lists and limit config
 * from the RMA store, resets to a blank issue when no initial issue is given, sets the modal title
 * from the editing flag, and validates and enforces the per-ticket device limit before saving.
 *
 * @param props - Modal props: handleUpdate, facilityId, replacedDevices, selectedFacility,
 *   initialDeviceIssue, listCurrentDeviceIds, listCurrentReplacementDeviceIds, index,
 *   hasProtectPlus, deviceIssueArray, onCancel and setTitle.
 * @returns An object carrying the modal state and setter, the edited device issue, editing flag,
 *   errors, maintenance flag, option builders and lists, and the add and field-change handlers.
 */
const useAddRmaDeviceModal = ({
  handleUpdate,
  facilityId,
  replacedDevices,
  selectedFacility,
  initialDeviceIssue,
  listCurrentDeviceIds,
  listCurrentReplacementDeviceIds,
  index,
  hasProtectPlus,
  deviceIssueArray,
  onCancel,
  setTitle,
}: Pick<
  AddRMADeviceModalProps,
  | 'handleUpdate'
  | 'facilityId'
  | 'replacedDevices'
  | 'selectedFacility'
  | 'initialDeviceIssue'
  | 'listCurrentDeviceIds'
  | 'listCurrentReplacementDeviceIds'
  | 'index'
  | 'hasProtectPlus'
  | 'deviceIssueArray'
  | 'onCancel'
  | 'setTitle'
>) => {
  const rmaOptionLists = useRmaStore((s) => s.rmaOptionLists);
  const rmaLimitConfig = useRmaStore((s) => s.rmaLimitConfig);

  const [state, setState] = useSetState<any>({
    deviceIssue: initialDeviceIssue,
    isEditing: !_.isEmpty(initialDeviceIssue),
    isKeepAdding: false,
    isWarning: false,
    errors: {
      ...INITIAL_ERRORS,
    },
  });

  const { deviceIssue, isEditing, errors } = state;

  const reasonOptions = withStoredDescribedReasons(
    getModelReasonOptions(rmaOptionLists.reasons, deviceIssue?.deviceModel),
    initialDeviceIssue?.reasons,
  );

  /**
   * Flags the RMA device currently selected in the modal as a maintenance case. True when the device's shouldMaintain flag is set or when any chosen reason is the protect-plus or return-for-maintenance reason.
   *
   * @returns True when the current device issue is a maintenance case.
   */
  const isMaintenanceDevice =
    deviceIssue?.shouldMaintain ||
    _.some(deviceIssue?.reasons, (x) =>
      [PROGRAM_REASON.PROTECT_PLUS, PROGRAM_REASON.RETURN_FOR_MAINTENANCE].includes(x.value),
    );

  const hasReturnToStock = _.includes(
    selectedFacility?.enabledPrograms,
    FACILITY_PROGRAMS.RETURN_TO_STOCK,
  );

  /**
   * Detects whether an RMA ticket exceeds the allowed number of devices that must be physically returned. Counts the issues whose reasons are not purely lost device, return to stock or closed account and compares that count against rmaLimitConfig.maxDevicePerTicket.
   *
   * @param clonedDeviceIssueArray - Device issues staged on the ticket, including the one being added.
   * @returns True when the returnable-device count exceeds the per-ticket limit.
   */
  const handleWarning = (clonedDeviceIssueArray: any) => {
    const array = [
      OTHER_RMA_REASONS.LOST_DEVICE,
      OTHER_RMA_REASONS.RETURN_TO_STOCK,
      OTHER_RMA_REASONS.CLOSED_ACCOUNT,
    ];
    return (
      _.countBy(clonedDeviceIssueArray, (d) =>
        _.some(d.reasons, (r) => !_.includes(array, r.value)),
      ).true > (rmaLimitConfig.maxDevicePerTicket ?? Infinity)
    );
  };

  /**
   * Commits the edited RMA device issue to the ticket, replacing the entry at the editing index or appending it to a deep clone of the list. Sets the warning flag and aborts when handleWarning finds the per-ticket returnable-device limit exceeded, otherwise runs handleValidate and either stores the errors or calls handleUpdate and then resets to a blank device issue while keep-adding is on, closing the modal when it is not.
   *
   * @returns Nothing.
   */
  const onClickAddButton = () => {
    const clonedDeviceIssueArray = _.cloneDeep(deviceIssueArray) || [];

    if (index || index === 0) {
      _.assign(clonedDeviceIssueArray[index], deviceIssue);
    } else {
      clonedDeviceIssueArray.push(deviceIssue);
    }

    if (handleWarning(clonedDeviceIssueArray)) {
      setState({ isWarning: true });
      return;
    }

    const errors = handleValidate(deviceIssue, reasonOptions);
    if (_.isEmpty(errors)) {
      handleUpdate(clonedDeviceIssueArray);
      if (state.isKeepAdding) {
        setState({
          deviceIssue: getInitialDeviceIssue(rmaOptionLists.patientImpacts),
          errors: {
            ...INITIAL_ERRORS,
          },
        });
      } else {
        onCancel?.();
      }
    } else {
      setState({ errors });
    }
  };

  /**
   * Fetches call-center devices matching a device-id search for the RMA add-device modal, scoped to the current facility and sorted by device id descending. Logs the error and resolves to an empty list when the query throws.
   *
   * @param searchInput - Partial device id used as the search filter.
   * @param limit - Maximum number of devices to fetch.
   * @returns The fetched device records, or an empty array on failure.
   */
  const fetchDevicesInfo = async (searchInput: any, limit: any) => {
    try {
      const queryFilter = {
        deviceId: searchInput,
        sortBy: 'deviceId',
        sortOrder: 'desc',
        facilityId: facilityId as string,
      };
      const deviceInfo = await fetchCallCenterDevices(queryFilter, limit);
      return deviceInfo;
    } catch (error) {
      console.error('Failed to fetch devices info: ', error);
      return [];
    }
  };

  /**
   * Loads device-id autocomplete options for the RMA add-device modal, returning nothing for blank input and otherwise searching up to ten call-center devices. Labels each option with the device id plus auto-event-detection text for MCT-capable devices outside test facilities, badges high sampling rate, maintenance, RMA, renewal and in-study, disables renewal and RMA devices, drops ids already on the ticket and groups the disabled ones under a non-selectable heading.
   *
   * @param inputValue - Device id text typed into the select.
   * @returns Device autocomplete options, with non-selectable devices in a trailing group.
   */
  const loadOptionsDevices = async (inputValue: any) => {
    const trimInput = String(inputValue).trim();
    if (!trimInput) {
      return [];
    }

    try {
      const dataLoaded = await fetchDevicesInfo(trimInput, 10);
      /**
       * The fetched devices reshaped into select options with status badges. Each entry gets a JSX label carrying the device id plus high-sampling-rate, maintenance-needed, RMA, renewal and in-study badges, an auto-event-detection suffix for non-test facilities on non-Biotres-Pro devices that support Biotres MCT, and a disabled flag for renewal or RMA devices.
       *
       * @returns Device option objects with label, value, model, disabled and shouldMaintain.
       */
      const formattedData = (dataLoaded ?? []).map((item: any) => {
        const {
          deviceId,
          canRunBiotresMCT,
          model,
          isRenewal,
          additionalStatus,
          shouldMaintain,
          lastSync,
          isRunningStudy,
        } = item;

        const isFacilityTest = selectedFacility.status === 'Test';
        const isBiotresPro = model === DEVICE.BIOTRES_PRO;
        const shouldDisplayAutoTag = !isFacilityTest && canRunBiotresMCT && !isBiotresPro;
        let deviceIdText = deviceId;
        const deviceModelTag = shouldDisplayAutoTag && t`Auto-event detection enabled`;
        if (deviceModelTag) {
          deviceIdText += ` (${deviceModelTag})`;
        }

        const isHighSamplingRate = checkUsingHighSamplingRateDevice(lastSync?.schema);
        const isRma = additionalStatus === 'RMA';
        const isProtectPlus = !isRma && shouldMaintain;
        const disabled = isRenewal || isRma;

        return {
          label: (
            <div className={classNames('device-item-container', disabled && '--disabled')}>
              <div className="device">{deviceIdText}</div>
              <div className="badge-list">
                {isHighSamplingRate && (
                  <div className="badge-item --hs">{t`High sampling rate`}</div>
                )}
                {isProtectPlus && (
                  <div className="badge-item --protect-plus">{t`Maintenance needed`}</div>
                )}
                {isRma && <div className="badge-item --rma">{t`RMA`}</div>}
                {isRenewal && <div className="badge-item --renewal">{t`Renewal`}</div>}
                {isRunningStudy && <div className="badge-item --in-study">{t`In study`}</div>}
              </div>
            </div>
          ),
          value: item.deviceId,
          model: item.model,
          disabled,
          shouldMaintain: item?.shouldMaintain,
        };
      });
      const filterDevice = filterDeviceIssue(formattedData, listCurrentDeviceIds);

      /**
       * The selectable device options from the filtered device list. Derived by keeping only the entries that are not disabled.
       *
       * @returns Device options that can be picked.
       */
      const devices = filterDevice.filter((e: any) => !e.disabled);
      /**
       * The device options that cannot be picked, such as renewal or already-RMA'd devices. Derived by keeping only the filtered entries flagged disabled.
       *
       * @returns Device options marked disabled.
       */
      const disabledDevices = filterDevice.filter((e: any) => e.disabled);

      if (!_.isEmpty(disabledDevices)) {
        devices.push({
          label: <div className="disabled-device-container">{t`Non-selectable devices`}</div>,
          options: disabledDevices,
        });
      }

      return devices;
    } catch (error) {
      console.error('Failed to fetch devices: ', error);
      return [];
    }
  };

  /**
   * Applies a device-id or replacement-device-id selection to the RMA device issue being edited. A device selection stores the option with its key, model and shouldMaintain flag, resets the replacement device and the reasons and pre-selects the protect-plus or return-for-maintenance reason for maintenance devices, while a replacement selection stores null for 'No replacement'; both clear that field's error and dismiss the device-limit warning.
   *
   * @param name - Field being changed, either deviceId or replacementDeviceId.
   * @param value - Selected value, whose key is kept for the device field.
   * @param option - Full selected option carrying model and shouldMaintain.
   * @returns Nothing.
   */
  const onChangeDeviceId = (name: any, value: any, option: any = {}) => {
    if (state.isWarning) {
      setState({ isWarning: false });
    }

    const clonedDeviceIssue = { ...deviceIssue };
    switch (name) {
      case 'deviceId': {
        const { ...rest } = option;
        _.assign(clonedDeviceIssue, {
          [name]: {
            ...rest,
            key: value.key,
          },
          replacementDeviceId: null,
          deviceModel: option?.model,
          shouldMaintain: option?.shouldMaintain,
          reasons: option?.shouldMaintain
            ? [
                toOptionEntry(getModelReasonOptions(rmaOptionLists.reasons, option?.model), {
                  value: hasProtectPlus
                    ? PROGRAM_REASON.PROTECT_PLUS
                    : PROGRAM_REASON.RETURN_FOR_MAINTENANCE,
                }),
              ]
            : [],
        });
        break;
      }
      case 'replacementDeviceId': {
        const isRealReplacement = option?.value !== 'No replacement';
        const hasLostDeviceReason = _.some(
          clonedDeviceIssue?.reasons,
          (reason) => reason.value === OTHER_RMA_REASONS.LOST_DEVICE,
        );
        _.assign(clonedDeviceIssue, {
          [name]: isRealReplacement ? option : null,
          inputValue: '',
          ...(isRealReplacement ? { lostDeviceNeedReplacement: hasLostDeviceReason } : {}),
        });
        break;
      }
      default: {
        break;
      }
    }

    setState({
      deviceIssue: clonedDeviceIssue,
      errors: {
        ...errors,
        [name]: '',
      },
    });
  };

  /**
   * Applies an RMA reason selection to the device issue, keeping the exclusive other-reasons apart from the combinable device issues. Picking lost device, return to stock or closed account last collapses the selection to that single reason, otherwise those three are stripped from the multi-selection; a reason that stays selected keeps its description, and the limit warning and field error are reset.
   *
   * @param name - Field holding the reasons, used for the value and its error key.
   * @param value - Reason values currently selected in the multi-select.
   * @returns Nothing.
   */
  const onChangeReasonRMA = (name: any, value: any) => {
    /**
     * The selected reason values reshaped into reason entries. Derived by keeping the entry of a reason that was already selected, so its description survives, and building a labelled entry from the option list for a newly selected one.
     *
     * @returns Reason entries with value, label and describe.
     */
    const arrayValue = _.map(
      value,
      (item) =>
        _.find(deviceIssue?.reasons, (reason) => reason.value === item) ??
        toOptionEntry(reasonOptions, { value: item }),
    );
    const clonedDeviceIssue = { ...deviceIssue };
    switch (_.last(value)) {
      case OTHER_RMA_REASONS.LOST_DEVICE:
      case OTHER_RMA_REASONS.RETURN_TO_STOCK:
      case OTHER_RMA_REASONS.CLOSED_ACCOUNT: {
        _.assign(clonedDeviceIssue, { [name]: [_.last(arrayValue)] });
        break;
      }
      default: {
        const array = [
          OTHER_RMA_REASONS.LOST_DEVICE,
          OTHER_RMA_REASONS.RETURN_TO_STOCK,
          OTHER_RMA_REASONS.CLOSED_ACCOUNT,
        ];
        _.remove(arrayValue, (item) => _.includes(array, item.value));
        _.assign(clonedDeviceIssue, { [name]: arrayValue });
        break;
      }
    }
    if (
      !_.find(clonedDeviceIssue?.reasons || [], (x) => x.value === OTHER_RMA_REASONS.LOST_DEVICE)
    ) {
      _.assign(clonedDeviceIssue, { lostDeviceNeedReplacement: false });
    } else if (!_.isNil(clonedDeviceIssue?.replacementDeviceId)) {
      _.assign(clonedDeviceIssue, { lostDeviceNeedReplacement: true });
    }

    setState({
      isWarning: false,
      deviceIssue: clonedDeviceIssue,
      errors: {
        ...errors,
        [name]: '',
      },
    });
  };

  /**
   * Stores the selected patient impact on the RMA device issue being edited. Clones the issue with the new value under the given field name and clears that field's validation error.
   *
   * @param name - Field holding the patient impact value.
   * @param value - Selected patient impact option.
   * @returns Nothing.
   */
  const onChangePatientImpact = (name: any, value: any) => {
    const clonedDeviceIssue = { ...deviceIssue };
    _.assign(clonedDeviceIssue, { [name]: value });
    setState({
      deviceIssue: clonedDeviceIssue,
      errors: {
        ...errors,
        [name]: '',
      },
    });
  };

  /**
   * Stores the free-text describe of one selected reason on the RMA device issue being edited. Replaces the describe of the reason with the given value, leaving the other reasons untouched, and clears that reason's describe error.
   *
   * @param reasonValue - Value of the reason the describe belongs to.
   * @param describe - Free text entered for the reason.
   * @returns Nothing.
   */
  const onChangeReasonDescribe = (reasonValue: string, describe: string) => {
    const clonedDeviceIssue = {
      ...deviceIssue,
      reasons: _.map(deviceIssue?.reasons, (reason) =>
        reason.value === reasonValue ? { ...reason, describe } : reason,
      ),
    };
    setState({
      deviceIssue: clonedDeviceIssue,
      errors: {
        ...errors,
        describes: { ...errors.describes, [reasonValue]: '' },
      },
    });
  };

  /**
   * Stores a new date of event on the RMA device issue being edited. Clones the current issue with the supplied date and clears the dateOfEvent validation error through setState.
   *
   * @param dateOfEvent - Newly picked event date.
   * @returns Nothing.
   */
  const onChangeDateOfEvent = (dateOfEvent: any) => {
    const clonedDeviceIssue = { ...deviceIssue };
    _.assign(clonedDeviceIssue, { dateOfEvent });
    setState({
      deviceIssue: clonedDeviceIssue,
      errors: {
        ...errors,
        dateOfEvent: '',
      },
    });
  };

  /**
   * Builds the grouped RMA reason options for a device model from the reason options in the RMA store. Splits the model's options into device issues and other reasons, then emits a disabled 'Device issues' heading followed by its options, a disabled 'Other reasons' heading followed by the other reasons, and last the selected reasons none of those offer, such as a program reason, so the select shows their labels.
   *
   * @param model - Device model whose reason options are selected.
   * @returns Reason select options with disabled group headings.
   */
  const generateOptionReasonForRMA = (model: any) => {
    const { deviceIssues, otherReasons } = splitReasonOptions(
      getModelReasonOptions(rmaOptionLists.reasons, model),
      hasReturnToStock,
    );

    const deviceIssuesTitle = {
      label: 'Device issues (select all that apply)',
      value: 'Device issues (select all that apply)',
      disabled: true,
    };
    const otherReasonsTitle = {
      label: 'Other reasons (select one, cannot combine with device issues)',
      value: 'Other reasons (select one, cannot combine with device issues)',
      disabled: true,
    };
    const selectedReasons: Pick<RmaOption, 'label' | 'value'>[] = deviceIssue?.reasons ?? [];
    const unofferedSelectedReasons = _.differenceBy(
      selectedReasons,
      [...deviceIssues, ...otherReasons],
      'value',
    );
    return [
      deviceIssuesTitle,
      ..._.map(deviceIssues, toSelectOption),
      otherReasonsTitle,
      ..._.map(otherReasons, toSelectOption),
      ..._.map(unofferedSelectedReasons, toSelectOption),
    ];
  };

  /**
   * Builds the replacement-device options for one RMA device issue, always starting with the no-replacement entry. Restricts the replaced-device pool to devices of the same model when the issue carries a deviceModel and offers all of them otherwise, then strips the replacement ids already chosen on other issues through filterDeviceIssue.
   *
   * @param deviceIssue - Device issue whose replacement options are built.
   * @returns Replacement-device select options.
   */
  const getReplaceDeviceOptions = (deviceIssue: any) => {
    const options = [NO_REPLACEMENT_OPTION];
    if (deviceIssue?.deviceModel) {
      _.forEach(replacedDevices, (replaceDevice) => {
        if (replaceDevice.model === deviceIssue?.deviceModel) {
          options.push({
            label: replaceDevice.deviceId,
            value: replaceDevice.deviceId,
          });
        }
      });
      return filterDeviceIssue(options, listCurrentReplacementDeviceIds);
    }
    _.forEach(replacedDevices, (replaceDevice) => {
      options.push({
        label: replaceDevice.deviceId,
        value: replaceDevice.deviceId,
      });
    });
    return filterDeviceIssue(options, listCurrentReplacementDeviceIds);
  };

  useLayoutEffect(() => {
    if (_.isEmpty(initialDeviceIssue)) {
      setState({
        deviceIssue: getInitialDeviceIssue(rmaOptionLists.patientImpacts),
        isEditing: false,
        errors: {
          ...INITIAL_ERRORS,
        },
      });
    }
  }, [initialDeviceIssue]);

  useLayoutEffect(() => {
    if (state.isEditing) {
      setTitle?.(t`Edit RMA device`);
    } else {
      setTitle?.(t`Add RMA device`);
    }
  }, [state.isEditing]);

  /**
   * The replacement-device options for the device issue currently being edited. Memoized from getReplaceDeviceOptions and recomputed when the renewal stock, the device issue or the already-used device ids change.
   *
   * @returns Replacement option objects.
   */
  const replacedDeviceOptions = useMemo(
    () => getReplaceDeviceOptions(deviceIssue),
    [replacedDevices, deviceIssue, listCurrentDeviceIds],
  );

  /**
   * The patient impact select options for the RMA modal. Memoized from the patient impact options in the RMA store by mapping each entry to its label and value, with the label repeated as the title the select shows for the picked option.
   *
   * @returns Patient impact options.
   */
  const patientImpactList = useMemo(
    () => _.map(rmaOptionLists.patientImpacts, toSelectOption),
    [rmaOptionLists.patientImpacts],
  );

  const describeOptions = getOptionsNeedingDescribe(deviceIssue?.reasons, reasonOptions);

  const deviceIdValue = useMemo(
    () =>
      deviceIssue?.deviceId?.isHighSamplingRate
        ? deviceIssue?.deviceId?.value
        : deviceIssue?.deviceId,
    [deviceIssue],
  );

  return {
    state,
    deviceIssue,
    isEditing,
    errors,
    isMaintenanceDevice,
    onClickAddButton,
    loadOptionsDevices,
    onChangeDeviceId,
    onChangeReasonRMA,
    onChangePatientImpact,
    onChangeReasonDescribe,
    describeOptions,
    onChangeDateOfEvent,
    generateOptionReasonForRMA,
    replacedDeviceOptions,
    patientImpactList,
    deviceIdValue,
    setState,
  };
};

export { useAddRmaDeviceModal };
